import {
  CombinedGraphQLErrors,
  ServerError,
} from "@apollo/client";
import type { ErrorLike } from "@apollo/client/core";

// ─── Error Classification ────────────────────────────────────────────────────

export enum AtlasErrorKind {
  Network = "Network",
  Timeout = "Timeout",
  RateLimit = "RateLimit",
  Server = "Server",
  NotFound = "NotFound",
  Code = "Code",
  Unknown = "Unknown",
}

export type NotFoundResource = "country" | "product";

/**
 * Synthetic error thrown client-side when an ID from the URL doesn't match
 * any known entity in the metadata. The Atlas GraphQL API returns an empty
 * data array (HTTP 200) for unknown IDs rather than an error, so "not found"
 * is a client-side concept.
 */
export class AtlasNotFoundError extends Error {
  readonly resource: NotFoundResource;
  readonly resourceId: string | number;

  constructor(resource: NotFoundResource, resourceId: string | number) {
    super(`${resource} not found: ${resourceId}`);
    this.name = "AtlasNotFoundError";
    this.resource = resource;
    this.resourceId = resourceId;
  }

  static is(err: unknown): err is AtlasNotFoundError {
    return (
      err instanceof AtlasNotFoundError ||
      (err instanceof Error && err.name === "AtlasNotFoundError")
    );
  }
}

interface AtlasErrorBase {
  message: string;
  originalError: ErrorLike;
  isRetryable: boolean;
}

interface AtlasHttpError extends AtlasErrorBase {
  kind:
    | AtlasErrorKind.Server
    | AtlasErrorKind.Timeout
    | AtlasErrorKind.Unknown;
  statusCode: number;
}

interface AtlasRateLimitError extends AtlasErrorBase {
  kind: AtlasErrorKind.RateLimit;
  statusCode: number;
  retryAfterSeconds?: number;
}

interface AtlasNotFoundClientError extends AtlasErrorBase {
  kind: AtlasErrorKind.NotFound;
  resource: NotFoundResource;
  resourceId: string | number;
}

interface AtlasNonHttpError extends AtlasErrorBase {
  kind: AtlasErrorKind.Network | AtlasErrorKind.Code;
}

export type AtlasError =
  | AtlasHttpError
  | AtlasRateLimitError
  | AtlasNotFoundClientError
  | AtlasNonHttpError;

function parseRetryAfter(error: ServerError): number | undefined {
  const header = error.response?.headers?.get?.("Retry-After");
  if (!header) return undefined;
  const seconds = Number.parseInt(header, 10);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : undefined;
}

/**
 * Classify an error from Apollo Client v4 into a user-facing error category.
 *
 * Apollo Client v4 uses discriminated error classes:
 * - CombinedGraphQLErrors: GraphQL validation/execution errors
 * - ServerError: HTTP errors with statusCode
 * - Plain Error / TypeError: network connectivity failures
 *
 * Also recognizes AtlasNotFoundError thrown by route/param validation.
 */
export function classifyError(error: ErrorLike): AtlasError {
  // Client-thrown "not found" for invalid country/product IDs in the URL
  if (AtlasNotFoundError.is(error)) {
    return {
      kind: AtlasErrorKind.NotFound,
      message: error.message,
      originalError: error,
      isRetryable: false,
      resource: error.resource,
      resourceId: error.resourceId,
    };
  }

  // GraphQL errors (validation, execution) → client-side bug
  if (CombinedGraphQLErrors.is(error)) {
    // The backend signals an unsupported region via a "Not a supported country."
    // execution error. Surface it as NotFound so the fallback UI offers "Reset
    // Selection" rather than a generic "try refreshing" message.
    const notSupported = error.errors.find((e) =>
      /not a supported country/i.test(e.message ?? ""),
    );
    if (notSupported) {
      const pathStr = Array.isArray(notSupported.path)
        ? notSupported.path.join(".")
        : "";
      const resource: NotFoundResource = /product/i.test(pathStr)
        ? "product"
        : "country";
      return {
        kind: AtlasErrorKind.NotFound,
        message: notSupported.message,
        originalError: error,
        isRetryable: false,
        resource,
        resourceId: "unknown",
      };
    }
    return {
      kind: AtlasErrorKind.Code,
      message: error.errors.map((e) => e.message).join("; "),
      originalError: error,
      isRetryable: false,
    };
  }

  // Server returned an HTTP error status
  if (ServerError.is(error)) {
    const { statusCode } = error;

    if (statusCode === 408) {
      return {
        kind: AtlasErrorKind.Timeout,
        message: error.message,
        originalError: error,
        isRetryable: true,
        statusCode,
      };
    }

    if (statusCode === 429) {
      return {
        kind: AtlasErrorKind.RateLimit,
        message: error.message,
        originalError: error,
        isRetryable: true,
        statusCode,
        retryAfterSeconds: parseRetryAfter(error),
      };
    }

    if ([500, 502, 503, 504].includes(statusCode)) {
      return {
        kind: AtlasErrorKind.Server,
        message: error.message,
        originalError: error,
        isRetryable: true,
        statusCode,
      };
    }

    // Other HTTP errors (4xx, etc.)
    return {
      kind: AtlasErrorKind.Unknown,
      message: error.message,
      originalError: error,
      isRetryable: false,
      statusCode,
    };
  }

  // Network connectivity failures (fetch failed, DNS, CORS, offline).
  // Intentionally *not* treating bare TypeErrors as network failures — a
  // "Cannot read properties of null" bug would otherwise show a misleading
  // "Connection Problem" screen with a Try Again button that can't recover.
  if (
    error.message?.includes("Failed to fetch") ||
    error.message?.includes("NetworkError") ||
    error.message?.includes("Network request failed")
  ) {
    return {
      kind: AtlasErrorKind.Network,
      message: error.message,
      originalError: error,
      isRetryable: true,
    };
  }

  return {
    kind: AtlasErrorKind.Unknown,
    message: error.message,
    originalError: error,
    isRetryable: false,
    statusCode: 0,
  };
}

// ─── User-Facing Messages ────────────────────────────────────────────────────

export interface HumanReadableMessage {
  title: string;
  message: string;
}

const staticMessages: Record<
  Exclude<AtlasErrorKind, AtlasErrorKind.RateLimit | AtlasErrorKind.NotFound>,
  HumanReadableMessage
> = {
  [AtlasErrorKind.Network]: {
    title: "Connection Problem",
    message:
      "We couldn't reach the server. Please check your internet connection and try again.",
  },
  [AtlasErrorKind.Timeout]: {
    title: "Request Timed Out",
    message:
      "The server is taking too long to respond. This may be due to heavy traffic — please try again shortly.",
  },
  [AtlasErrorKind.Server]: {
    title: "Server Error",
    message:
      "Something went wrong on our end. Our team has been notified. Please try again in a moment.",
  },
  [AtlasErrorKind.Code]: {
    title: "Something Went Wrong",
    message:
      "We encountered an unexpected issue. Try refreshing the page or selecting different options.",
  },
  [AtlasErrorKind.Unknown]: {
    title: "Something Went Wrong",
    message:
      "An unexpected error occurred. Please try again or refresh the page.",
  },
};

export function getHumanReadableMessage(
  error: AtlasError,
): HumanReadableMessage {
  if (error.kind === AtlasErrorKind.RateLimit) {
    const extra =
      error.retryAfterSeconds !== undefined
        ? ` You can try again in about ${error.retryAfterSeconds} second${error.retryAfterSeconds === 1 ? "" : "s"}.`
        : "";
    return {
      title: "Too Many Requests",
      message: `The Atlas is experiencing heavy traffic right now. Please wait a moment and refresh the page.${extra}`,
    };
  }

  if (error.kind === AtlasErrorKind.NotFound) {
    const resource = error.resource;
    const label = resource === "country" ? "Region" : "Product";
    const noun = resource === "country" ? "region" : "product";
    return {
      title: `${label} Not Found`,
      message: `The selected ${noun} doesn't match any ${noun} in our dataset. Try selecting a different ${noun} or reset selections.`,
    };
  }

  return staticMessages[error.kind];
}

// ─── Recovery Actions ────────────────────────────────────────────────────────

export interface RecoveryAction {
  label: string;
  action: "retry" | "reload" | "reset-params";
}

export function getRecoveryAction(error: AtlasError): RecoveryAction {
  if (error.kind === AtlasErrorKind.NotFound) {
    return { label: "Reset Selection", action: "reset-params" };
  }
  if (error.isRetryable) {
    return { label: "Try Again", action: "retry" };
  }
  return { label: "Refresh Page", action: "reload" };
}
